import { type RefObject, useEffect, useMemo, useRef, useState } from 'react'
import { Textarea } from '@/components/ui/shadcn/textarea'
import { Button } from '@/components/ui/shadcn/button'
import { Loader2Icon } from 'lucide-react'
import { RocketIcon } from '@/components/ui/icons/RocketIcon'
import { type LayoutNode } from '@backend/lib/db/typeRoots/layoutNode'
import { type ImageModel } from '@backend/lib/ai/functions/generateImage'
import { useKeyBindings } from '@/hooks/useKeyBindings'
import { type Role } from '@/pages/workspace-page/workspace-page'
import { ImageModelPicker, type ModelUpgradeLimit } from '@/components/ui/image-model-picker'

interface Props {
  imageModel: ImageModel
  onModelChange: (model: ImageModel) => void
  onSubmit: (userPrompt: string, context: Role | 'image_generation' | null) => void
  onBlur?: () => void
  file?: File
  onFileChange?: (file: File) => void
  inputButtonRef?: RefObject<HTMLDivElement | null>
  selectedNode?: LayoutNode | null
  isGenerateImagePressed?: boolean
  isSuggestionLoading?: boolean
  isGeneratingImage?: boolean
  isFreePlan?: boolean
  onUpgradeClick?: (limit: ModelUpgradeLimit) => void
}

export function WorkspaceEditorPromptBox({
  imageModel,
  onModelChange,
  onSubmit,
  onBlur,
  inputButtonRef,
  selectedNode,
  isGenerateImagePressed,
  isGeneratingImage,
  isFreePlan,
  onUpgradeClick,
}: Props) {
  const textAreaRef = useRef<HTMLTextAreaElement>(null)
  const [value, setValue] = useState('')
  const [isFocused, setIsFocused] = useState(false)
  const userManuallyChangedModelRef = useRef(false)

  useEffect(() => {
    if (textAreaRef.current) {
      textAreaRef.current.style.height = '40px'
      textAreaRef.current.style.height = Math.min(textAreaRef.current.scrollHeight, 120) + 'px'
    }
  }, [value])

  const selectedNodeIsLoading = selectedNode?.data.isLoading

  const isCurrentElementLoading = useMemo(() => {
    if (isGeneratingImage) return true
    if (!selectedNode) {
      return false
    }
    return selectedNode.data.isLoading
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedNode, selectedNodeIsLoading, isGeneratingImage])

  const currentElement = useMemo(() => {
    if (isGenerateImagePressed) return 'image_generation'
    if (selectedNode?.metadata?.role === 'image') return 'image'
    return null
  }, [isGenerateImagePressed, selectedNode?.metadata?.role])

  const isSubmitDisabled = isCurrentElementLoading || (currentElement === 'image' && !value.trim())

  useKeyBindings(isFocused, {
    enter: (e) => {
      if (e.metaKey || e.ctrlKey) {
        if (isSubmitDisabled) return
        onSubmit(value, currentElement as Role | 'image_generation' | null)
      }
    },
  })

  const buttonText = useMemo(() => {
    if (currentElement === 'image_generation') return 'Generate'
    if (currentElement === 'image') return 'Edit Image'
    return 'Run'
  }, [currentElement])

  // Switch to gemini when entering image edit context
  // Only auto-switch if user hasn't manually changed the model
  useEffect(() => {
    if (currentElement === 'image' && !userManuallyChangedModelRef.current) {
      if (imageModel === 'flux') {
        onModelChange('gemini')
      }
    }
  }, [currentElement, imageModel, onModelChange])

  useEffect(() => {
    setValue('')
    setIsFocused(false)
    userManuallyChangedModelRef.current = false
  }, [currentElement])

  const handleManualModelChange = (model: ImageModel) => {
    userManuallyChangedModelRef.current = true
    onModelChange(model)
  }

  const baseStyles = 'w-[690px] flex flex-col gap-2 outline-hidden'
  const positionStyles = 'absolute bottom-12 left-1/2 -translate-x-1/2'
  const transitionStyles = 'transform transition-all duration-200 ease-in-out origin-bottom'
  const visibilityStyles = {
    visible: 'opacity-100 translate-y-0 pointer-events-auto',
    hidden: 'opacity-0 translate-y-full pointer-events-none',
  }

  const getVisibilityStyle = () => {
    if (selectedNode?.metadata?.role === 'logo') {
      return visibilityStyles.hidden
    }

    if (selectedNode?.data.type === 'text' || selectedNode?.data.type === 'shape') {
      return visibilityStyles.hidden
    }

    if (!selectedNode && !isGenerateImagePressed) {
      return visibilityStyles.hidden
    }

    return visibilityStyles.visible
  }

  const styles = {
    editor: `
            ${baseStyles}
            ${positionStyles}
            ${transitionStyles}
            ${!isFocused ? 'scale-[0.8]' : 'scale-100'}
            ${getVisibilityStyle()}
        `,
  }

  return (
    <>
      <div
        ref={inputButtonRef}
        className={styles.editor}
        onBlur={(e) => {
          if (inputButtonRef?.current?.contains(e.relatedTarget as Node)) {
            return
          }
          setIsFocused(false)
          onBlur?.()
        }}
        onClick={() => {
          setIsFocused(true)
        }}
        tabIndex={0}>
        <div className={`bg-background border-primary flex flex-col rounded-lg border p-2`}>
          <Textarea
            ref={textAreaRef}
            value={value}
            onChange={(e) => setValue(e.target.value)}
            className="text-foreground placeholder:text-foreground/50 focus:outline-hidden resize-none overflow-auto border-none bg-transparent"
            placeholder={
              currentElement === 'image_generation'
                ? 'Image Prompt (leave blank to let the AI generate image based on the caption)'
                : `Type to change ${currentElement}...`
            }
          />
          <div className={`flex w-full items-center justify-end`}>
            <div className="flex items-center gap-2">
              {(currentElement === 'image_generation' || currentElement === 'image') && (
                <ImageModelPicker
                  value={imageModel}
                  onChange={handleManualModelChange}
                  disabled={isCurrentElementLoading}
                  isFreePlan={isFreePlan}
                  onUpgradeClick={onUpgradeClick}
                />
              )}
              <Button
                variant="primary"
                onMouseDown={(e) => {
                  e.preventDefault()
                }}
                onClick={() => {
                  onSubmit(value, currentElement as Role | 'image_generation' | null)
                }}
                disabled={isSubmitDisabled}>
                {isCurrentElementLoading ? (
                  <Loader2Icon className="animate-infinite h-4 w-4 animate-spin" />
                ) : (
                  <>
                    <RocketIcon />
                    {buttonText}
                  </>
                )}
              </Button>
            </div>
          </div>
        </div>
      </div>
    </>
  )
}
